// src/composer/ui/NameEditor.tsx
import { useState } from 'react';
import type { BuildState } from '../../packs/types';
import type { IdentityAdapter } from '../../adapters/types';

interface NameEditorProps {
  build: BuildState;
  identity: IdentityAdapter;
  namingLabel?: string;
  onSave: (name: string, tagline: string) => void;
  onClose: () => void;
}

const NAME_MAX = 24;
const TAGLINE_MAX = 64;
const NAME_PATTERN = /^[A-Za-z0-9 _-]*$/;
const TAGLINE_PATTERN = /^[\x20-\x7e]*$/;

export function NameEditor({ build, identity, namingLabel = 'Ship', onSave, onClose }: NameEditorProps) {
  const [name, setName] = useState(build.name ?? '');
  const [tagline, setTagline] = useState(build.tagline ?? '');
  const user = identity.user();

  const handleNameChange = (v: string) => {
    if (NAME_PATTERN.test(v) && v.length <= NAME_MAX) setName(v);
  };
  const handleTaglineChange = (v: string) => {
    if (TAGLINE_PATTERN.test(v) && v.length <= TAGLINE_MAX) setTagline(v);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-slate-900 rounded-xl p-6 w-80 flex flex-col gap-4"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400">Name Your {namingLabel}</h2>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-400">Name ({name.length}/{NAME_MAX})</label>
          <input
            className="bg-slate-800 rounded px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-amber-500"
            value={name}
            placeholder="Untitled"
            onChange={e => handleNameChange(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-400">Tagline ({tagline.length}/{TAGLINE_MAX})</label>
          <input
            className="bg-slate-800 rounded px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-amber-500"
            value={tagline}
            placeholder="Fast and deadly"
            onChange={e => handleTaglineChange(e.target.value)}
          />
        </div>
        {user && (
          <button
            className="text-xs text-slate-400 hover:text-white text-left"
            onClick={() => handleTaglineChange(`by @${user.displayName}`)}
          >
            Fill as &quot;by @{user.displayName}&quot;
          </button>
        )}
        <div className="flex gap-2 justify-end">
          <button className="px-4 py-2 text-sm text-slate-400 hover:text-white" onClick={onClose}>Cancel</button>
          <button
            className="px-4 py-2 text-sm bg-amber-500 hover:bg-amber-400 text-black rounded font-medium"
            onClick={() => { onSave(name, tagline); onClose(); }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
